/**
* Localized FAQ copy for /{locale}/faq (6 locales).
*
* Every answer is self-contained (readable standalone when an AI answer
* engine quotes it) and price-free in meta strings — prices are allowed in
* body answers, never in title/description (see src/lib/seo-copy.ts).
*/

export interface LocaleFaqItem {
  q: string;
  a: string;
}

export interface LocaleFaqCopy {
  locale: string;
  metaTitle: string;
  metaDesc: string;
  eyebrow: string;
  h1: string;
  h1Accent: string;
  subtitle: string;
  answerSummary: string;
  breadcrumbHome: string;
  breadcrumbSelf: string;
  ctaPrimary: string;
  ctaSecondary: string;
  badges: string[];
  closingTitle: string;
  closingBody: string;
  faq: LocaleFaqItem[];
}

export const LOCALE_FAQ: Record<string, LocaleFaqCopy> = {
  en: {
    locale: "en",
    metaTitle: "Notary Public Thailand FAQ — Legalisation, Translation, Visa | Thai Notary Law",
    metaDesc:
      "Straight answers from licensed Thai Notarial Services Attorneys: who can notarise in Thailand, MFA and embassy legalisation steps, turnaround times, and how to book. Talk to our team today.",
    eyebrow: "FAQ",
    h1: "Frequently asked questions about",
    h1Accent: "notary services in Thailand",
    subtitle:
      "Answers written by licensed Notarial Services Attorneys, aligned with the Lawyers Council of Thailand, the Department of Consular Affairs and the main embassies in Bangkok.",
    answerSummary:
      "In Thailand only a lawyer licensed by the Lawyers Council of Thailand as a Notarial Services Attorney may notarise documents. Most documents used abroad also need Department of Consular Affairs (MFA) legalisation and, for non-Hague countries, destination embassy legalisation. Standard notarisation takes about 30 minutes in office; the full legalisation chain takes 7-14 business days.",
    breadcrumbHome: "Home",
    breadcrumbSelf: "FAQ",
    ctaPrimary: "Ask us on LINE",
    ctaSecondary: "Call the office",
    badges: ["Updated 2026", "Answered by licensed attorneys", "English-speaking team"],
    closingTitle: "Still have a question?",
    closingBody:
      "Send us a photo of your document and the name of the authority that will receive it. We confirm the exact chain of certifications, the documents you must bring, and the realistic timeline, usually within 30 minutes during office hours.",
    faq: [
      {
        q: "Who is allowed to act as a Notary Public in Thailand?",
        a: "Thailand has no separate notary profession. Notarisation is performed by a Notarial Services Attorney: a lawyer licensed by the Lawyers Council of Thailand under the 2008 Notarial Services Attorney Regulations. All six attorneys on our team hold that licence, and the licence number appears on every certificate we issue so a foreign authority can verify it.",
      },
      {
        q: "Can a notarised Thai document be used abroad immediately?",
        a: "Usually not. After notarisation, most receiving authorities require legalisation by the Department of Consular Affairs of the Thai Ministry of Foreign Affairs. Countries that are not party to the Hague Apostille Convention additionally require legalisation by their embassy in Bangkok. We run the whole chain — notarisation, certified translation, MFA, embassy — as a single file so you only hand over documents once.",
      },
      {
        q: "How long does each step take?",
        a: "Notarisation is completed in about 30 minutes at the office, with a 15-minute express option. Certified translation is normally 24-48 hours. MFA legalisation is 2-3 business days standard or 1 business day express. Embassy legalisation depends on the embassy, typically 3-10 business days. A full notary-to-embassy chain therefore runs 7-14 business days.",
      },
      {
        q: "Do I have to sign in front of the attorney?",
        a: "Yes for signature authentication. Thai regulations require the signatory to sign in the physical presence of the Notarial Services Attorney, with a valid passport or Thai ID. Certified true copies and document certifications do not require your signature, so those can be handled by an authorised representative carrying your ID copy and a signed authorisation letter.",
      },
      {
        q: "I am outside Thailand. What are my options?",
        a: "You have three routes. Sign at a Royal Thai Embassy or Consulate in your country; have a local notary in your country notarise and then obtain an Apostille; or appoint a representative in Thailand with a power of attorney that has itself been notarised and legalised abroad. Send us the requirement in writing and we will tell you which route the receiving authority will accept.",
      },
      {
        q: "Which documents are most commonly notarised?",
        a: "Powers of attorney, affidavits and statutory declarations, certified copies of passports and ID cards, academic transcripts and degree certificates, company registration documents and board resolutions, bank statements and sponsorship letters, marriage and birth certificates, and signature verification for property or share transfers.",
      },
      {
        q: "Do you provide certified translations as well?",
        a: "Yes. We translate and certify in more than 40 languages, including English, Chinese, Japanese, Korean, German, French, Spanish, Russian, Vietnamese, Indonesian, Malay and Arabic. Translations are prepared so they are accepted by the Thai MFA and by embassies, which reject translations that lack the translator declaration and matching document numbering.",
      },
      {
        q: "Do you serve clients outside Bangkok?",
        a: "We operate staffed offices in Bangkok (Wang Thonglang), Khon Kaen and Udon Thani, and work with partner attorneys covering all 77 provinces. Outside those offices we meet clients by appointment at an agreed location; those meeting points are appointment-only and are not branch offices.",
      },
      {
        q: "What does it cost?",
        a: "Fees are quoted per file rather than published, because the number of originals, the target language and the number of legalisation rounds change the work involved. Government and embassy fees are charged at cost and listed separately on the quotation, so nothing is marked up inside a package price. Ask for a written quotation by phone, LINE or email.",
      },
      {
        q: "What happens if the embassy rejects the document?",
        a: "We correct the notarisation and the translation at no additional professional fee until the document is accepted; only the government or embassy fee for the resubmission is charged again. Before we start, we check the current requirements of the receiving authority precisely to avoid that situation.",
      },
      {
        q: "How is my personal data handled?",
        a: "Copies of certified documents are retained as required by the Lawyers Council of Thailand and processed under the Thai Personal Data Protection Act B.E. 2562 (2019). Files are access-controlled, we do not share client documents with third parties without instruction, and you can request a certified copy of your own file later.",
      },
      {
        q: "How do I book an appointment?",
        a: "Message us on LINE, call the office between 09:00 and 18:00 daily, or send the document by email. We give a written scope, fee estimate and timeline, usually within 30 minutes during office hours, before any work begins.",
      },
    ],
  },

  zh: {
    locale: "zh",
    metaTitle: "泰国公证常见问题 — 认证、翻译、签证全流程 | Thai Notary Law",
    metaDesc:
      "由泰国律师公会注册公证律师解答：谁能在泰国办理公证、外交部与使馆认证流程、办理时间与预约方式。中文团队全程协助，欢迎咨询。",
    eyebrow: "常见问题",
    h1: "关于泰国公证服务的",
    h1Accent: "常见问题解答",
    subtitle:
      "答案依据泰国律师公会规定、外交部领事司要求以及曼谷各国使馆的最新受理标准撰写。",
    answerSummary:
      "在泰国，只有取得泰国律师公会公证律师资格的执业律师才能办理公证。用于境外的文件通常还需经泰国外交部领事司认证；非海牙公约国家还需目的国驻曼谷使馆认证。现场公证约需 30 分钟，完整认证链条一般为 7 至 14 个工作日。",
    breadcrumbHome: "首页",
    breadcrumbSelf: "常见问题",
    ctaPrimary: "LINE 咨询",
    ctaSecondary: "电话联系",
    badges: ["2026 年更新", "注册公证律师解答", "中文服务"],
    closingTitle: "还有其他问题？",
    closingBody:
      "请把文件照片和收件机构名称发给我们，我们会确认所需的完整认证链条、需要携带的材料以及实际所需时间，办公时间内通常 30 分钟内回复。",
    faq: [
      {
        q: "在泰国谁有资格办理公证？",
        a: "泰国没有独立的公证人职业，公证由「公证律师」办理，即依据 2551 年（2008 年）公证律师规定取得泰国律师公会认证的执业律师。我们团队六位律师均持有该资格，出具的每份证书上都标注律师执照编号，方便境外机构核验。",
      },
      {
        q: "公证后的文件能直接在国外使用吗？",
        a: "通常不能。公证后多数收件机构还要求泰国外交部领事司认证；若目的国不是海牙公约成员国，还需该国驻曼谷使馆认证。我们提供公证、认证翻译、外交部、使馆的一站式办理，客户只需交付一次文件。",
      },
      {
        q: "各个环节需要多长时间？",
        a: "现场公证约 30 分钟，可选 15 分钟加急。认证翻译一般 24 至 48 小时。外交部认证标准 2 至 3 个工作日，加急 1 个工作日。使馆认证视各馆规定，通常 3 至 10 个工作日。因此完整链条约 7 至 14 个工作日。",
      },
      {
        q: "必须本人到场签字吗？",
        a: "签字认证必须本人到场，依泰国规定须在公证律师面前签署，并出示有效护照或泰国身份证。副本核实与文件证明类业务不需要本人签字，可由受托人携带身份证件复印件及授权书代办。",
      },
      {
        q: "我人不在泰国怎么办？",
        a: "有三种方式：在所在国的泰国大使馆或领事馆签署；由当地公证人公证后办理海牙认证；或出具经境外公证与认证的授权书，委托泰国代理人办理。请将收件机构的书面要求发给我们，我们会告知哪种方式会被接受。",
      },
      {
        q: "哪些文件最常办理公证？",
        a: "授权委托书、宣誓书与声明书、护照及身份证副本核实、学历成绩单与毕业证书、公司注册文件与董事会决议、银行流水与担保函、结婚证与出生证明，以及房产或股权转让的签字认证。",
      },
      {
        q: "你们也提供认证翻译吗？",
        a: "提供。我们可翻译并认证 40 多种语言，包括英语、中文、日语、韩语、德语、法语、西班牙语、俄语、越南语、印尼语、马来语和阿拉伯语。译文按外交部与使馆要求编排，包含译者声明与与原件一致的编号，避免被退件。",
      },
      {
        q: "曼谷以外地区能办理吗？",
        a: "我们在曼谷（Wang Thonglang）、孔敬和乌隆他尼设有实体办公室，并与合作律师覆盖全泰 77 府。其他地区采取预约见面方式，这些会面地点仅供预约使用，并非分公司。",
      },
      {
        q: "费用大约多少？",
        a: "我们按案件出具报价，不在网站公布价格，因为文件份数、目标语言与认证环节数量都会影响工作量。政府与使馆规费按实际金额单列，不会加价隐藏在套餐价格中。可通过电话、LINE 或邮件索取书面报价。",
      },
      {
        q: "如果使馆退件怎么办？",
        a: "我们免费修改公证与译文直到通过，仅需重新缴纳政府或使馆规费。办理前我们会先核对收件机构的最新要求，尽量避免退件情况发生。",
      },
      {
        q: "个人资料如何保护？",
        a: "认证文件副本依泰国律师公会规定保存，并依 2562 年（2019 年）个人资料保护法处理。档案设有访问权限控制，未经指示不会向第三方提供客户文件，客户日后可申请调取自己的档案副本。",
      },
      {
        q: "如何预约？",
        a: "可通过 LINE 联系、每日 09:00 至 18:00 致电办公室，或将文件发送至邮箱。在开始办理前，我们会先提供书面的服务范围、费用预估与时间表，办公时间内通常 30 分钟内回复。",
      },
    ],
  },

  ja: {
    locale: "ja",
    metaTitle: "タイの公証（ノータリー）よくある質問 — 認証・翻訳・ビザ | Thai Notary Law",
    metaDesc:
      "タイ弁護士会登録の公証弁護士が回答。タイで公証できるのは誰か、外務省・大使館認証の流れ、所要日数、予約方法まで。日本語対応でご相談を承ります。",
    eyebrow: "よくある質問",
    h1: "タイの公証サービスに関する",
    h1Accent: "よくあるご質問",
    subtitle:
      "タイ弁護士会の規則、外務省領事局の要件、バンコク主要大使館の最新の受理基準に基づいて公証弁護士が回答しています。",
    answerSummary:
      "タイで公証を行えるのは、タイ弁護士会に公証弁護士として登録された弁護士のみです。海外で使用する書類は通常、外務省領事局の認証が追加で必要で、ハーグ条約非加盟国向けにはバンコクの当該国大使館認証も必要になります。公証自体は事務所で約30分、認証チェーン全体では7〜14営業日が目安です。",
    breadcrumbHome: "ホーム",
    breadcrumbSelf: "よくある質問",
    ctaPrimary: "LINEで相談する",
    ctaSecondary: "電話で問い合わせる",
    badges: ["2026年更新", "登録公証弁護士が回答", "日本語対応"],
    closingTitle: "解決しないご質問がありますか？",
    closingBody:
      "書類の写真と提出先機関名をお送りください。必要な認証の順序、当日ご持参いただくもの、現実的な所要日数をご案内します。営業時間内は通常30分以内に回答しています。",
    faq: [
      {
        q: "タイで公証を行えるのは誰ですか？",
        a: "タイには独立した公証人制度がなく、公証は「公証弁護士（Notarial Services Attorney）」が行います。これは2008年の公証弁護士規則に基づきタイ弁護士会の資格を得た弁護士です。当事務所の弁護士6名全員が同資格を保有し、発行する証明書には弁護士登録番号を記載するため、海外機関での確認が可能です。",
      },
      {
        q: "公証済みの書類はそのまま海外で使えますか？",
        a: "多くの場合そのままでは使えません。公証後にタイ外務省領事局の認証が必要となり、ハーグ条約非加盟国ではさらにバンコクの当該国大使館認証が求められます。当事務所は公証、認証翻訳、外務省、大使館までを一括で処理するため、書類のお預けは一度で済みます。",
      },
      {
        q: "各手続きにどれくらいかかりますか？",
        a: "公証は事務所で約30分、15分の特急対応も可能です。認証翻訳は通常24〜48時間。外務省認証は標準2〜3営業日、特急1営業日。大使館認証は館により3〜10営業日程度です。したがって全体では7〜14営業日が目安となります。",
      },
      {
        q: "本人が弁護士の面前で署名する必要がありますか？",
        a: "署名認証については必要です。タイの規則により、有効なパスポートまたはタイIDを提示のうえ、公証弁護士の面前で署名しなければなりません。原本証明や書類証明は署名不要のため、身分証の写しと委任状を持参した代理人でも対応できます。",
      },
      {
        q: "タイ国外にいる場合はどうすればよいですか？",
        a: "方法は3つあります。居住国のタイ大使館・総領事館で署名する、現地の公証人で公証しアポスティーユを取得する、または海外で公証・認証を受けた委任状によりタイ国内の代理人に依頼する方法です。提出先の要件を文面でお送りいただければ、受理される方法をご案内します。",
      },
      {
        q: "よく公証される書類は何ですか？",
        a: "委任状、宣誓供述書、パスポートやIDの原本証明、成績証明書・学位記、会社登記書類や取締役会議事録、残高証明やスポンサーレター、婚姻証明書・出生証明書、不動産や株式譲渡に伴う署名認証などです。",
      },
      {
        q: "認証翻訳も依頼できますか？",
        a: "可能です。英語、中国語、日本語、韓国語、ドイツ語、フランス語、スペイン語、ロシア語、ベトナム語、インドネシア語、マレー語、アラビア語など40言語以上に対応します。翻訳者宣誓文と原本と一致する番号付けを備えた、外務省・大使館で受理される形式で作成します。",
      },
      {
        q: "バンコク以外でも対応していますか？",
        a: "バンコク（ワントンラン）、コンケン、ウドンタニに常駐オフィスがあり、提携弁護士により全77県をカバーしています。それ以外の地域では事前予約による面談で対応します。これらの面談場所は予約専用であり、支店ではありません。",
      },
      {
        q: "費用はどれくらいですか？",
        a: "料金は案件ごとのお見積りとし、サイト上では公開していません。原本の通数、対象言語、認証の段階数によって作業量が変わるためです。政府手数料および大使館手数料は実費で見積書に別記し、パッケージ料金に上乗せして隠すことはありません。お電話、LINE、メールで書面のお見積りをご依頼ください。",
      },
      {
        q: "大使館で受理されなかった場合はどうなりますか？",
        a: "受理されるまで公証および翻訳を追加の弁護士報酬なしで修正します。再提出に伴う政府・大使館の実費手数料のみご負担いただきます。着手前に提出先の最新要件を確認するため、こうした事態は基本的に避けられます。",
      },
      {
        q: "個人情報はどのように扱われますか？",
        a: "認証書類の写しはタイ弁護士会の規定に従って保管し、2019年タイ個人情報保護法（PDPA）に基づいて取り扱います。ファイルはアクセス制限下に置かれ、ご指示なく第三者に提供することはありません。後日ご自身のファイルの写しを請求することもできます。",
      },
      {
        q: "予約はどのように行いますか？",
        a: "LINEでのご連絡、毎日09:00〜18:00の電話、またはメールでの書類送付が可能です。作業開始前に、業務範囲・概算費用・スケジュールを書面でご提示します。営業時間内は通常30分以内にご返答します。",
      },
    ],
  },

  ko: {
    locale: "ko",
    metaTitle: "태국 공증 자주 묻는 질문 — 인증·번역·비자 절차 | Thai Notary Law",
    metaDesc:
      "태국 변호사협회 등록 공증변호사가 직접 답변합니다. 공증 자격, 외교부·대사관 인증 절차, 소요 기간, 예약 방법까지. 한국어 상담 문의 환영합니다.",
    eyebrow: "자주 묻는 질문",
    h1: "태국 공증 서비스에 대한",
    h1Accent: "자주 묻는 질문",
    subtitle:
      "태국 변호사협회 규정, 외교부 영사국 요건, 방콕 주요 대사관의 최신 접수 기준에 따라 공증변호사가 작성한 답변입니다.",
    answerSummary:
      "태국에서는 변호사협회에 공증변호사로 등록된 변호사만 공증을 할 수 있습니다. 해외에서 사용할 서류는 대개 외교부 영사국 인증이 추가로 필요하며, 헤이그협약 비가입국은 방콕 주재 해당국 대사관 인증까지 받아야 합니다. 공증 자체는 사무실에서 약 30분, 전체 인증 절차는 보통 7~14영업일이 소요됩니다.",
    breadcrumbHome: "홈",
    breadcrumbSelf: "자주 묻는 질문",
    ctaPrimary: "LINE으로 문의",
    ctaSecondary: "전화 문의",
    badges: ["2026년 업데이트", "등록 공증변호사 답변", "한국어 상담 가능"],
    closingTitle: "궁금한 점이 더 있으신가요?",
    closingBody:
      "서류 사진과 제출 기관명을 보내주시면 필요한 인증 순서, 준비물, 실제 소요 기간을 확인해 드립니다. 영업시간 내에는 보통 30분 이내에 답변드립니다.",
    faq: [
      {
        q: "태국에서 공증을 할 수 있는 사람은 누구인가요?",
        a: "태국에는 별도의 공증인 제도가 없으며, 2008년 공증변호사 규정에 따라 태국 변호사협회의 자격을 취득한 변호사(Notarial Services Attorney)가 공증을 수행합니다. 저희 사무소 변호사 6인 모두 해당 자격을 보유하고 있으며, 발급 서류에 변호사 등록번호를 기재하여 해외 기관이 확인할 수 있도록 합니다.",
      },
      {
        q: "공증받은 서류를 해외에서 바로 사용할 수 있나요?",
        a: "대부분은 불가능합니다. 공증 이후 태국 외교부 영사국의 인증이 필요하며, 헤이그협약 비가입국의 경우 방콕 주재 해당국 대사관 인증까지 요구됩니다. 저희는 공증, 인증번역, 외교부, 대사관 절차를 한 번에 처리하므로 서류를 한 번만 맡기시면 됩니다.",
      },
      {
        q: "각 단계에 걸리는 시간은 얼마인가요?",
        a: "공증은 사무실에서 약 30분이며 15분 특급도 가능합니다. 인증번역은 보통 24~48시간, 외교부 인증은 표준 2~3영업일 또는 특급 1영업일입니다. 대사관 인증은 관할에 따라 3~10영업일 정도이므로 전체 절차는 7~14영업일이 일반적입니다.",
      },
      {
        q: "반드시 변호사 앞에서 서명해야 하나요?",
        a: "서명 인증은 그렇습니다. 태국 규정상 유효한 여권 또는 태국 신분증을 제시하고 공증변호사 면전에서 서명해야 합니다. 원본대조필이나 서류 증명은 본인 서명이 필요 없으므로, 신분증 사본과 위임장을 지참한 대리인이 처리할 수 있습니다.",
      },
      {
        q: "태국에 있지 않은 경우 어떻게 하나요?",
        a: "세 가지 방법이 있습니다. 거주국의 주태국 대사관·영사관에서 서명하거나, 현지 공증인의 공증 후 아포스티유를 받거나, 해외에서 공증·인증된 위임장으로 태국 내 대리인을 지정하는 방법입니다. 제출처의 요건을 문서로 보내주시면 어떤 방법이 인정되는지 안내해 드립니다.",
      },
      {
        q: "가장 많이 공증하는 서류는 무엇인가요?",
        a: "위임장, 진술서 및 선서서, 여권·신분증 원본대조필, 성적증명서와 학위증, 법인등기 서류와 이사회 결의서, 잔고증명서와 재정보증서, 혼인·출생증명서, 부동산 및 주식 양도를 위한 서명 인증 등입니다.",
      },
      {
        q: "인증번역도 함께 제공하나요?",
        a: "제공합니다. 영어, 중국어, 일본어, 한국어, 독일어, 프랑스어, 스페인어, 러시아어, 베트남어, 인도네시아어, 말레이어, 아랍어 등 40개 이상 언어를 번역·인증합니다. 번역자 선서문과 원본과 일치하는 번호 체계를 갖추어 외교부와 대사관에서 반려되지 않도록 작성합니다.",
      },
      {
        q: "방콕 이외 지역도 가능한가요?",
        a: "방콕(왕텅랑), 콘깬, 우돈타니에 상주 사무실이 있으며 제휴 변호사를 통해 전국 77개 주를 지원합니다. 그 외 지역은 사전 예약 후 협의된 장소에서 만나 처리하며, 이러한 미팅 장소는 예약 전용으로 지점이 아닙니다.",
      },
      {
        q: "비용은 어느 정도인가요?",
        a: "비용은 사이트에 공개하지 않고 건별 견적으로 안내합니다. 원본 부수, 대상 언어, 인증 단계 수에 따라 업무량이 달라지기 때문입니다. 정부 및 대사관 수수료는 실비로 견적서에 별도 표기하며 패키지 가격에 얹어 숨기지 않습니다. 전화, LINE, 이메일로 서면 견적을 요청하실 수 있습니다.",
      },
      {
        q: "대사관에서 반려되면 어떻게 되나요?",
        a: "접수될 때까지 공증과 번역을 추가 변호사 보수 없이 수정해 드립니다. 재제출에 따른 정부·대사관 실비 수수료만 부담하시면 됩니다. 착수 전 제출 기관의 최신 요건을 확인하므로 이러한 상황은 거의 발생하지 않습니다.",
      },
      {
        q: "개인정보는 어떻게 관리되나요?",
        a: "인증 서류 사본은 태국 변호사협회 규정에 따라 보관하며 2019년 태국 개인정보보호법(PDPA)에 따라 처리합니다. 파일은 접근 통제 하에 보관되고 지시 없이 제3자에게 제공하지 않으며, 이후 본인 파일 사본을 요청하실 수 있습니다.",
      },
      {
        q: "예약은 어떻게 하나요?",
        a: "LINE 메시지, 매일 09:00~18:00 사무실 전화, 또는 이메일로 서류를 보내주시면 됩니다. 작업 시작 전에 업무 범위, 예상 비용, 일정을 서면으로 안내하며 영업시간 내 보통 30분 이내에 회신합니다.",
      },
    ],
  },

  vi: {
    locale: "vi",
    metaTitle: "Câu hỏi thường gặp về công chứng tại Thái Lan | Thai Notary Law",
    metaDesc:
      "Luật sư công chứng được Liên đoàn Luật sư Thái Lan cấp phép giải đáp: ai được công chứng, quy trình hợp pháp hóa Bộ Ngoại giao và đại sứ quán, thời gian xử lý, cách đặt lịch. Liên hệ ngay.",
    eyebrow: "Câu hỏi thường gặp",
    h1: "Câu hỏi thường gặp về",
    h1Accent: "dịch vụ công chứng tại Thái Lan",
    subtitle:
      "Nội dung do luật sư công chứng biên soạn, dựa trên quy định của Liên đoàn Luật sư Thái Lan, Cục Lãnh sự Bộ Ngoại giao và các đại sứ quán chính tại Bangkok.",
    answerSummary:
      "Tại Thái Lan, chỉ luật sư được Liên đoàn Luật sư cấp phép làm Notarial Services Attorney mới được công chứng. Giấy tờ dùng ở nước ngoài thường phải hợp pháp hóa tại Cục Lãnh sự Bộ Ngoại giao, và với quốc gia chưa tham gia Công ước Hague còn cần hợp pháp hóa tại đại sứ quán ở Bangkok. Công chứng mất khoảng 30 phút tại văn phòng; toàn bộ chuỗi hợp pháp hóa thường 7-14 ngày làm việc.",
    breadcrumbHome: "Trang chủ",
    breadcrumbSelf: "Câu hỏi thường gặp",
    ctaPrimary: "Nhắn tin LINE",
    ctaSecondary: "Gọi văn phòng",
    badges: ["Cập nhật 2026", "Luật sư có giấy phép giải đáp", "Hỗ trợ tiếng Việt"],
    closingTitle: "Bạn còn câu hỏi khác?",
    closingBody:
      "Hãy gửi ảnh giấy tờ và tên cơ quan tiếp nhận. Chúng tôi sẽ xác nhận đúng trình tự chứng nhận cần thiết, giấy tờ phải mang theo và thời gian thực tế, thường trong vòng 30 phút giờ làm việc.",
    faq: [
      {
        q: "Ai được phép công chứng tại Thái Lan?",
        a: "Thái Lan không có nghề công chứng viên riêng. Việc công chứng do Notarial Services Attorney thực hiện, tức luật sư được Liên đoàn Luật sư Thái Lan cấp phép theo Quy định về Luật sư công chứng năm 2008. Cả sáu luật sư của chúng tôi đều có giấy phép này và số giấy phép được ghi trên mọi giấy chứng nhận để cơ quan nước ngoài kiểm tra.",
      },
      {
        q: "Giấy tờ đã công chứng có dùng được ngay ở nước ngoài không?",
        a: "Thường là không. Sau công chứng, phần lớn cơ quan tiếp nhận yêu cầu hợp pháp hóa tại Cục Lãnh sự Bộ Ngoại giao Thái Lan. Với quốc gia chưa tham gia Công ước Hague, cần thêm hợp pháp hóa tại đại sứ quán nước đó ở Bangkok. Chúng tôi xử lý trọn chuỗi nên bạn chỉ cần giao giấy tờ một lần.",
      },
      {
        q: "Mỗi bước mất bao lâu?",
        a: "Công chứng khoảng 30 phút tại văn phòng, có tùy chọn nhanh 15 phút. Dịch thuật có chứng nhận thường 24-48 giờ. Hợp pháp hóa Bộ Ngoại giao tiêu chuẩn 2-3 ngày làm việc hoặc nhanh 1 ngày. Hợp pháp hóa đại sứ quán tùy từng nơi, thường 3-10 ngày làm việc. Tổng cộng khoảng 7-14 ngày làm việc.",
      },
      {
        q: "Tôi có bắt buộc ký trước mặt luật sư không?",
        a: "Có, đối với chứng thực chữ ký. Quy định Thái Lan yêu cầu người ký phải ký trực tiếp trước mặt luật sư công chứng, xuất trình hộ chiếu hoặc thẻ căn cước Thái còn hiệu lực. Chứng thực bản sao không cần chữ ký của bạn, nên người được ủy quyền có thể thay mặt xử lý.",
      },
      {
        q: "Tôi đang ở ngoài Thái Lan thì làm thế nào?",
        a: "Có ba cách: ký tại Đại sứ quán hoặc Tổng lãnh sự quán Thái Lan ở nước bạn; công chứng tại công chứng viên địa phương rồi xin Apostille; hoặc lập giấy ủy quyền đã được công chứng và hợp pháp hóa ở nước ngoài để người đại diện tại Thái Lan thực hiện. Gửi cho chúng tôi yêu cầu bằng văn bản của nơi tiếp nhận để được tư vấn cách phù hợp.",
      },
      {
        q: "Những giấy tờ nào thường được công chứng nhất?",
        a: "Giấy ủy quyền, bản khai có tuyên thệ, bản sao hộ chiếu và căn cước, bảng điểm và bằng cấp, hồ sơ đăng ký doanh nghiệp và nghị quyết hội đồng quản trị, sao kê ngân hàng và thư bảo lãnh, giấy đăng ký kết hôn và giấy khai sinh, cùng chứng thực chữ ký cho chuyển nhượng bất động sản hoặc cổ phần.",
      },
      {
        q: "Các bạn có dịch thuật công chứng không?",
        a: "Có. Chúng tôi dịch và chứng nhận hơn 40 ngôn ngữ, gồm tiếng Anh, Trung, Nhật, Hàn, Đức, Pháp, Tây Ban Nha, Nga, Việt, Indonesia, Mã Lai và Ả Rập. Bản dịch được trình bày theo chuẩn Bộ Ngoại giao và đại sứ quán, có tuyên bố của người dịch và đánh số khớp với bản gốc.",
      },
      {
        q: "Ngoài Bangkok có phục vụ không?",
        a: "Chúng tôi có văn phòng thường trực tại Bangkok (Wang Thonglang), Khon Kaen và Udon Thani, cùng mạng lưới luật sư đối tác phủ khắp 77 tỉnh. Ngoài các văn phòng này, chúng tôi gặp khách theo lịch hẹn tại địa điểm thỏa thuận; các điểm hẹn đó chỉ phục vụ theo hẹn và không phải chi nhánh.",
      },
      {
        q: "Chi phí khoảng bao nhiêu?",
        a: "Phí dịch vụ tùy theo loại giấy tờ (chứng thực chữ ký và bản sao, dịch có chứng nhận, lý lịch tư pháp) — liên hệ đội ngũ của chúng tôi để nhận báo giá. Lệ phí nhà nước và đại sứ quán được tính theo thực tế và ghi riêng trong báo giá, không cộng thêm vào giá trọn gói.",
      },
      {
        q: "Nếu đại sứ quán từ chối hồ sơ thì sao?",
        a: "Chúng tôi chỉnh sửa phần công chứng và bản dịch miễn phí dịch vụ cho đến khi hồ sơ được chấp nhận; bạn chỉ trả lại lệ phí nhà nước hoặc đại sứ quán cho lần nộp lại. Trước khi bắt đầu, chúng tôi luôn kiểm tra yêu cầu hiện hành của nơi tiếp nhận.",
      },
      {
        q: "Dữ liệu cá nhân được xử lý ra sao?",
        a: "Bản sao hồ sơ được lưu theo quy định của Liên đoàn Luật sư Thái Lan và xử lý theo Luật Bảo vệ dữ liệu cá nhân Thái Lan B.E. 2562 (2019). Hồ sơ được kiểm soát truy cập, không chia sẻ với bên thứ ba nếu không có chỉ dẫn, và bạn có thể xin bản sao hồ sơ của mình sau này.",
      },
      {
        q: "Đặt lịch hẹn thế nào?",
        a: "Nhắn LINE, gọi văn phòng từ 09:00 đến 18:00 hằng ngày, hoặc gửi giấy tờ qua email. Chúng tôi gửi phạm vi công việc, dự toán chi phí và tiến độ bằng văn bản trước khi bắt đầu, thường trong 30 phút giờ làm việc.",
      },
    ],
  },

  id: {
    locale: "id",
    metaTitle: "Tanya Jawab Notaris di Thailand — Legalisasi & Terjemahan | Thai Notary Law",
    metaDesc:
      "Dijawab oleh Notarial Services Attorney berlisensi: siapa yang boleh menotariskan di Thailand, alur legalisasi Kemenlu dan kedutaan, lama proses, serta cara membuat janji temu. Hubungi tim kami.",
    eyebrow: "Tanya Jawab",
    h1: "Pertanyaan yang sering diajukan tentang",
    h1Accent: "layanan notaris di Thailand",
    subtitle:
      "Jawaban disusun oleh Notarial Services Attorney berlisensi, mengacu pada aturan Lawyers Council of Thailand, Departemen Konsuler Kemenlu, dan kedutaan besar utama di Bangkok.",
    answerSummary:
      "Di Thailand hanya advokat berlisensi Notarial Services Attorney dari Lawyers Council of Thailand yang boleh menotariskan dokumen. Dokumen untuk dipakai di luar negeri umumnya masih perlu legalisasi Departemen Konsuler Kemenlu, dan untuk negara non-Hague juga legalisasi kedutaan di Bangkok. Notarisasi memakan sekitar 30 menit di kantor; rantai legalisasi penuh biasanya 7-14 hari kerja.",
    breadcrumbHome: "Beranda",
    breadcrumbSelf: "Tanya Jawab",
    ctaPrimary: "Tanya lewat LINE",
    ctaSecondary: "Telepon kantor",
    badges: ["Diperbarui 2026", "Dijawab advokat berlisensi", "Layanan bahasa Indonesia"],
    closingTitle: "Masih ada pertanyaan?",
    closingBody:
      "Kirimkan foto dokumen dan nama instansi penerima. Kami akan memastikan urutan pengesahan yang dibutuhkan, berkas yang harus dibawa, dan perkiraan waktu yang realistis, umumnya dalam 30 menit pada jam kerja.",
    faq: [
      {
        q: "Siapa yang boleh bertindak sebagai notaris di Thailand?",
        a: "Thailand tidak memiliki profesi notaris tersendiri. Notarisasi dilakukan oleh Notarial Services Attorney, yaitu advokat berlisensi dari Lawyers Council of Thailand berdasarkan Peraturan Notarial Services Attorney tahun 2008. Keenam advokat kami memegang lisensi tersebut dan nomor lisensi tercantum pada setiap sertifikat agar dapat diverifikasi instansi asing.",
      },
      {
        q: "Apakah dokumen yang sudah dinotariskan langsung bisa dipakai di luar negeri?",
        a: "Umumnya belum. Setelah notarisasi, sebagian besar instansi penerima meminta legalisasi Departemen Konsuler Kemenlu Thailand. Negara yang bukan anggota Konvensi Apostille Den Haag juga meminta legalisasi kedutaannya di Bangkok. Kami menangani seluruh rantai tersebut sehingga dokumen cukup diserahkan satu kali.",
      },
      {
        q: "Berapa lama tiap tahap berlangsung?",
        a: "Notarisasi sekitar 30 menit di kantor, dengan opsi ekspres 15 menit. Terjemahan tersertifikasi umumnya 24-48 jam. Legalisasi Kemenlu 2-3 hari kerja reguler atau 1 hari kerja ekspres. Legalisasi kedutaan bergantung kedutaannya, biasanya 3-10 hari kerja. Total rantai sekitar 7-14 hari kerja.",
      },
      {
        q: "Apakah saya harus tanda tangan di hadapan advokat?",
        a: "Ya untuk pengesahan tanda tangan. Aturan Thailand mewajibkan penanda tangan hadir langsung di hadapan Notarial Services Attorney dengan paspor atau KTP Thailand yang berlaku. Legalisir salinan tidak memerlukan tanda tangan Anda, sehingga dapat diwakilkan dengan surat kuasa dan salinan identitas.",
      },
      {
        q: "Bagaimana jika saya sedang berada di luar Thailand?",
        a: "Ada tiga jalur: menandatangani di Kedutaan atau Konsulat Thailand di negara Anda; menotariskan di notaris setempat lalu mengurus Apostille; atau menunjuk perwakilan di Thailand melalui surat kuasa yang sudah dinotariskan dan dilegalisasi di luar negeri. Kirimkan persyaratan tertulis instansi penerima agar kami dapat menunjukkan jalur yang akan diterima.",
      },
      {
        q: "Dokumen apa yang paling sering dinotariskan?",
        a: "Surat kuasa, afidavit dan pernyataan tersumpah, legalisir salinan paspor dan KTP, transkrip nilai dan ijazah, dokumen pendirian perusahaan dan risalah rapat direksi, rekening koran dan surat sponsor, akta nikah dan akta kelahiran, serta pengesahan tanda tangan untuk pengalihan properti atau saham.",
      },
      {
        q: "Apakah tersedia terjemahan tersertifikasi?",
        a: "Tersedia. Kami menerjemahkan dan mengesahkan lebih dari 40 bahasa, termasuk Inggris, Mandarin, Jepang, Korea, Jerman, Prancis, Spanyol, Rusia, Vietnam, Indonesia, Melayu, dan Arab. Terjemahan disusun agar diterima Kemenlu dan kedutaan, lengkap dengan pernyataan penerjemah dan penomoran yang sesuai dokumen asli.",
      },
      {
        q: "Apakah melayani di luar Bangkok?",
        a: "Kami memiliki kantor dengan staf tetap di Bangkok (Wang Thonglang), Khon Kaen, dan Udon Thani, serta jaringan advokat mitra yang mencakup 77 provinsi. Di luar kantor tersebut kami bertemu klien sesuai janji temu di lokasi yang disepakati; titik temu itu hanya berdasarkan janji dan bukan kantor cabang.",
      },
      {
        q: "Berapa perkiraan biayanya?",
        a: "Biaya layanan kantor diberikan melalui penawaran dari staf setelah dokumen diperiksa. Biaya resmi pemerintah dan kedutaan dibebankan sesuai tarif asli dan dirinci terpisah di penawaran.",
      },
      {
        q: "Bagaimana jika kedutaan menolak dokumen?",
        a: "Kami memperbaiki notarisasi dan terjemahan tanpa biaya jasa tambahan sampai dokumen diterima; hanya biaya resmi pengajuan ulang yang dibebankan kembali. Sebelum mulai, kami selalu memeriksa persyaratan terbaru instansi penerima untuk menghindari penolakan.",
      },
      {
        q: "Bagaimana data pribadi saya dikelola?",
        a: "Salinan dokumen tersertifikasi disimpan sesuai ketentuan Lawyers Council of Thailand dan diproses berdasarkan Undang-Undang Perlindungan Data Pribadi Thailand B.E. 2562 (2019). Berkas dilindungi kontrol akses, tidak dibagikan ke pihak ketiga tanpa instruksi, dan Anda dapat meminta salinan berkas Anda di kemudian hari.",
      },
      {
        q: "Bagaimana cara membuat janji temu?",
        a: "Hubungi kami lewat LINE, telepon kantor setiap hari pukul 09.00-18.00, atau kirim dokumen melalui email. Sebelum pekerjaan dimulai kami memberikan ruang lingkup, estimasi biaya, dan jadwal secara tertulis, umumnya dalam 30 menit pada jam kerja.",
      },
    ],
  },
};
